import { useCallback, useEffect, useRef } from 'react';

import {
  handleFullUpdate,
  handlePartialUpdate,
  setConnectionStatus,
  setError,
} from 'redux/slice/group-operations/GroupOperations.slice';
import { GroupOperationsSSEEvent } from 'redux/types/GroupOperations.types';
import { callPlannerEndPoints } from 'services/api/endPoints';
import { useAppDispatch } from 'utils/hooks/useAppDispatch';

interface UseGroupOperationsSSEProps {
  agencyId: string;
  allocatorId: string;
  userId: string;
  enabled: boolean;
}

export const useGroupOperationsSSE = ({
  agencyId,
  allocatorId,
  userId,
  enabled,
}: UseGroupOperationsSSEProps) => {
  const dispatch = useAppDispatch();
  const eventSourceRef = useRef<EventSource | null>(null);

  const handleSSEMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const data: GroupOperationsSSEEvent = JSON.parse(event.data);

        requestAnimationFrame(() => {
          if (data.type === 'FULL_UPDATE') {
            dispatch(
              handleFullUpdate({
                stats: data.stats,
                jobs: data.jobs,
              })
            );
          } else if (data.type === 'PARTIAL_UPDATE') {
            dispatch(handlePartialUpdate(data.job));
          }
        });
      } catch (error) {
        console.error('Error parsing GROUP_OPERATIONS SSE message:', error);
        dispatch(setError('Failed to parse SSE message'));
      }
    },
    [dispatch]
  );

  const handleSSEError = useCallback(
    (error: Event) => {
      console.error('GROUP_OPERATIONS SSE error:', error);
      dispatch(setConnectionStatus(false));
      dispatch(setError('SSE connection error'));
    },
    [dispatch]
  );

  const handleSSEOpen = useCallback(() => {
    dispatch(setConnectionStatus(true));
  }, [dispatch]);

  useEffect(() => {
    if (!enabled || !agencyId || !allocatorId || !userId) {
      return;
    }

    try {
      const sseUrl = callPlannerEndPoints.GROUP_OPERATIONS_SSE(
        agencyId,
        allocatorId,
        userId
      );
      const eventSource = new EventSource(sseUrl, {
        withCredentials: false,
      });

      eventSourceRef.current = eventSource;

      eventSource.onopen = (event) => {
        handleSSEOpen();
      };

      eventSource.onerror = (event) => {
        handleSSEError(event);
      };

      eventSource.onmessage = (event) => {
        handleSSEMessage(event);
      };

      eventSource.addEventListener('INITIAL_GROUP_OPERATIONS', (event) => {
        handleSSEMessage(event);
      });

      eventSource.addEventListener('GROUP_OPERATION_UPDATE', (event) => {
        handleSSEMessage(event);
      });

      return () => {
        eventSource.onopen = null;
        eventSource.onerror = null;
        eventSource.onmessage = null;
        eventSource.removeEventListener(
          'INITIAL_GROUP_OPERATIONS',
          handleSSEMessage
        );
        eventSource.removeEventListener(
          'GROUP_OPERATION_UPDATE',
          handleSSEMessage
        );
        eventSource.close();
        eventSourceRef.current = null;
        dispatch(setConnectionStatus(false));
      };
    } catch (error) {
      console.error('Failed to initialize GROUP_OPERATIONS SSE:', error);
      dispatch(setError('Failed to initialize SSE connection'));
    }
  }, [
    agencyId,
    allocatorId,
    userId,
    enabled,
    dispatch,
    handleSSEMessage,
    handleSSEError,
    handleSSEOpen,
  ]);

  return {
    isConnected: eventSourceRef.current?.readyState === EventSource.OPEN,
  };
};
